import * as React from 'react'
import { useTranslation } from 'i18n'
import { cn } from 'utils/cn'
import { calendarDaysUntilDue } from 'utils/loan-due-days'

export type LoanStatus = 'pending' | 'active' | 'returned' | 'expired'

export type LoanBadgeProps = {
  status?: LoanStatus | null
  dueAt?: string | null
  className?: string
}

export const isOpenLoan = (status?: LoanStatus | null): boolean =>
  status === 'pending' || status === 'active'

export const isClosedLoan = (status?: LoanStatus | null): boolean =>
  status === 'returned' || status === 'expired'

export const useLoanBadgeLabel = (
  status?: LoanStatus | null,
  dueAt?: string | null
): string | null => {
  const { t } = useTranslation()

  if (isOpenLoan(status)) {
    const daysLeft = calendarDaysUntilDue(dueAt)
    if (daysLeft === null) return t('Emprestado')
    if (daysLeft <= 0) return t('Vence hoje')
    if (daysLeft === 1) return t('Vence amanhã')
    return t('Emprestado')
  }

  if (status === 'returned') return t('Devolvido')
  if (status === 'expired') return t('Expirado')

  return null
}

const LoanBadge: React.FC<LoanBadgeProps> = ({ status, dueAt, className }) => {
  const label = useLoanBadgeLabel(status, dueAt)

  if (!label) return null

  const daysLeft = calendarDaysUntilDue(dueAt)
  const isDueSoon = isOpenLoan(status) && daysLeft !== null && daysLeft <= 1

  return (
    <span
      className={cn(
        'pointer-events-none absolute left-2 top-2 z-10 inline-flex h-6 items-center rounded-full px-2 text-[14px] font-bold whitespace-nowrap',
        isDueSoon && 'bg-warning text-gray-900',
        !isDueSoon && isOpenLoan(status) && 'bg-gray-900 text-white',
        isClosedLoan(status) && 'bg-gray-700 text-white',
        className
      )}
      data-testid="loan-badge"
    >
      {label}
    </span>
  )
}

export { LoanBadge }
